/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { createDialogRoute, DataSet, DateTimeTypes } from '@gh/common-shared';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  DialogService,
  noAnswer,
  NotificationService,
  STYLES_DIALOG_LAYOUT,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { LeadListItem, QuoteDetailsBusinessService, QuoteDetailsDialogConfig } from '../../../leads';
import {
  CloneQuote,
  cloneQuoteComparator,
  CloneSalesFolder,
  cloneSalesFolderComparator, CloneSite, cloneSiteComparator,
  getCloneQuoteId,
  getCloneSalesFolderId, getCloneSiteId,
} from '../../../leads/resources/quote.model';
import { gridCollectionComparator, GridDefinition, GridDefinitionFactory } from '@gh/core-ui-grid';
import { TranslationService } from '@gh/core-mls';
import { asString } from '@gh/core-util';
import { ID } from '@gh/common-shared-data';
import { createDefaultGridModel } from '@gh/common-internal-shared';
import { first, isNil } from 'lodash';

export enum QuoteDialogEntryPoint { CreateQuote, CloneQuote }

@Component({
             selector: 'gh-quote-details-dialog',
             template: require('./quote-details-dialog.component.html'),
             styles: [STYLES_DIALOG_LAYOUT],
           })
export class QuoteDetailsDialogComponent implements DialogInput<QuoteDetailsDialogConfig>, OnInit {
  input: QuoteDetailsDialogConfig;

  customer?: LeadListItem;
  salesFolders = new DataSet<CloneSalesFolder>(getCloneSalesFolderId, cloneSalesFolderComparator);
  sites = new DataSet<CloneSite>(getCloneSiteId, cloneSiteComparator);

  salesFolderLoading = false;
  siteLoading = false;

  showOverdue: boolean;
  showInactive: boolean;
  showCompleted: boolean;

  salesFolderGridDefinition: GridDefinition;
  siteGridDefinition: GridDefinition;

  private selectedSalesFolder?: CloneSalesFolder;
  private selectedSite?: CloneSite;

  constructor(private dialogRef: MatDialogRef<any>,
              private translationService: TranslationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private businessService: QuoteDetailsBusinessService,
              private notificationService: NotificationService,
             ) {

  }

  get title(): string {
    return {
      [QuoteDialogEntryPoint.CloneQuote]: 'lbl_rfq_clone_quote',
      [QuoteDialogEntryPoint.CreateQuote]: 'lbl_create_quote',
    }[this.input.entryPoint];
  }

  get isCloneEp(): boolean {
    return this.input.entryPoint === QuoteDialogEntryPoint.CloneQuote;
  }

  get selectedSalesFolderId(): Maybe<ID> {
    const {selectedSalesFolder} = this;

    return selectedSalesFolder ? selectedSalesFolder.salesFolderId : void 0;
  }

  get selectedSiteId(): Maybe<ID> {
    const {selectedSite} = this;

    return selectedSite ? selectedSite.id : void 0;
  }

  ngOnInit(): void {
    this.salesFolderGridDefinition = this.gridDefinitionFactory.buildManualWithModel({
     columns: [{
       field: 'name',
       headerName: this.translationService.get('lbl_name'),
       width: 300,
       comparator: gridCollectionComparator(cloneSalesFolderComparator),
       valueGetter: ({data}: { data: CloneSalesFolder }) =>
         `${DateTimeTypes.ShortDate.format(data.created)} - ${asString(data.name)}`,
     }],
     model: createDefaultGridModel({
       getId: getCloneSalesFolderId,
     }),
     options: {rowSelection: 'single'},
   });

    this.siteGridDefinition = this.gridDefinitionFactory.buildManualWithModel({
     columns: [{
       field: 'name',
       headerName: this.translationService.get('lbl_name'),
       width: 300,
       comparator: gridCollectionComparator(cloneSiteComparator),
       valueGetter: ({data}: { data: CloneSite }) =>
         `${DateTimeTypes.ShortDate.format(data.inspectedDate)} - ${asString(data.name)}`,
     }],
     model: createDefaultGridModel({
       getId: getCloneSiteId,
     }),
     options: {rowSelection: 'single'},
   });

  }

  loadContacts(event: AutocompleteLoadMoreEvent): void {
    event.load(this.businessService.findLeadsByName(event.query, {
      showCompleted: !!this.showCompleted,
      showOverdue: !!this.showOverdue,
      showInactive: !!this.showInactive,
    }));
  }

  onContactSelected(contact?: LeadListItem): void {
    this.selectedSalesFolder = void 0;

    this.salesFolders.clear();

    if (isNil(contact)) {
      return;
    }

    this.salesFolderLoading = true;
    this.businessService.findAllCloneSalesFoldersByCustomer(contact.contactId).subscribe(
      (salesFolders) => {
        this.salesFolderLoading = false;
        this.salesFolders.set(salesFolders);
        this.selectedSalesFolder = first(this.salesFolders.data);

        if (this.selectedSalesFolderId) {
          this.onSalesFolderSelected(this.selectedSalesFolderId);
        }
      },
      (errors) => {
        this.salesFolderLoading = false;
        this.notificationService.processAll(errors);
      },
    );
  }

  onSalesFolderSelected(id?: ID): void {
    if (isNil(id)) {
      return;
    }

    this.selectedSalesFolder = this.salesFolders.byId(id);

    this.siteLoading = true;
    this.businessService.findAllCloneSitesBySalesFolder(id).subscribe(
      (sites) => {
        this.siteLoading = false;
        this.sites.set(sites);
        this.selectedSite = first(this.sites.data);
      },
      (errors) => {
        this.siteLoading = false;
        this.notificationService.processAll(errors);
      },
    );
  }

  // onQuoteSelected(id: ID): void {
  //   this.selectedQuote = this.quotes.byId(id);
  // }

  // onClone(): void {
  //   const {selectedQuote, selectedSalesFolder} = this;
  //
  //   if (isNil(selectedQuote) || isNil(selectedSalesFolder)) {
  //     return;
  //   }
  //
  //   const cloned$ = this.cloneData(
  //     selectedSalesFolder.salesFolderId,
  //     selectedQuote.id,
  //     {skipWithdrawnWarningCheck: false});
  //
  //   cloned$.pipe(
  //     this.waitingService.operator(),
  //     catchError$((errors) => {
  //       if (errors.has('msg_pb_withdrawn_warning')) {
  //         const answer$ = this.dialogService.confirmation(yesNoConfirmation('msg_pb_withdrawn_warning'));
  //         const yesAnswer$ = answer$.pipe(filter$(yesAnswer), switchMap$(() => this.cloneData(
  //           selectedSalesFolder.salesFolderId,
  //           selectedQuote.id,
  //           {skipWithdrawnWarningCheck: true})));
  //         const noAnswer$ = answer$.pipe(filter$(noAnswer),
  //                                        mapTo$(throwError$('msg_pb_withdrawn_denied')),
  //                                        switchAll$());
  //
  //         return concat$(yesAnswer$, noAnswer$);
  //       } else {
  //         return throwError$(errors);
  //       }
  //     })).subscribe(
  //     (quote) => this.dialogRef.close(quote),
  //     this.notificationService.processAll);
  // }

  onCancel(): void {
    this.dialogRef.close();
  }

  onShowOverdueChange(event: any): void {
  }

  onShowInactiveChange(event: any): void {
    if (event.checked) {
      this.showCompleted = false;
    }
  }

  onShowCompletedChange(event: any): void {
    if (event.checked) {
      this.showInactive = false;
    }
  }
  // private cloneData(sfId: ID, quoteId: ID, request: QuoteCloneRequest): Observable<any> {
  //   switch (this.input.entryPoint) {
  //     case QuoteCloneDialogEntryPoint.CloneQuote:
  //       return this.businessService.cloneQuote(this.input.salesFolderId, quoteId, request);
  //     case QuoteCloneDialogEntryPoint.CloneChanges:
  //       return this.businessService.cloneVariations(
  //         this.input.salesFolderId,
  //         this.input.targetQuoteId!, // tslint:disable-line:no-non-null-assertion
  //         {
  //           sourceQuoteId: quoteId,
  //           targetQuoteVersion: this.input.targetQuoteVersion!, // tslint:disable-line:no-non-null-assertion
  //         });
  //     default:
  //       throw new Error(`Unknown entry point: ${this.input.entryPoint}`);
  //   }
  // }
}

export const quoteDetailsDialogRoute = createDialogRoute({
  component: QuoteDetailsDialogComponent,
});
